import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 60 * 1000;
const PING_MS = 5 * 60 * 1000;
const KEY = 'panelLastActivity';
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

const lastActivity = () => Number(localStorage.getItem(KEY) || Date.now());
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Logs panel users out after 30 min without activity, warning 1 min before; keeps the server session alive while active
export const PanelSession = () => {
  const navigate = useNavigate();
  const [left, setLeft] = useState(null);
  const lastPing = useRef(Date.now());

  const logout = useCallback((reason = 'idle') => {
    const token = localStorage.getItem('token');
    if (token) axios.post(`${API}/auth/logout`, {}, auth()).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem(KEY);
    navigate(`/login?sessao=${reason}`);
  }, [navigate]);

  const keepAlive = useCallback(() => {
    localStorage.setItem(KEY, String(Date.now()));
    lastPing.current = Date.now();
    axios.get(`${API}/auth/me`, auth()).catch(() => {});
    setLeft(null);
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, String(Date.now()));
    let mark = 0;
    const onAct = () => {
      const n = Date.now();
      if (n - mark < 2000) return;
      mark = n;
      if (IDLE_MS - (n - lastActivity()) > WARN_MS) localStorage.setItem(KEY, String(n));
    };
    EVENTS.forEach((e) => window.addEventListener(e, onAct, { passive: true }));
    const tick = setInterval(() => {
      const idle = Date.now() - lastActivity();
      if (idle >= IDLE_MS) { logout('idle'); return; }
      setLeft(idle >= IDLE_MS - WARN_MS ? Math.ceil((IDLE_MS - idle) / 1000) : null);
      if (idle < PING_MS && Date.now() - lastPing.current > PING_MS) {
        lastPing.current = Date.now();
        axios.get(`${API}/auth/me`, auth()).catch(() => {});
      }
    }, 1000);
    const icpt = axios.interceptors.response.use((r) => r, (err) => {
      const d = err.response?.data?.detail;
      if (err.response?.status === 401 && typeof d === 'string' && d.startsWith('Sessão encerrada')) logout('expired');
      return Promise.reject(err);
    });
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onAct));
      clearInterval(tick);
      axios.interceptors.response.eject(icpt);
    };
  }, [logout]);

  if (left == null) return null;
  return (
    <div data-testid="idle-warning-modal" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(10,13,18,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: 14, padding: '1.5rem', maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a202c', marginBottom: '0.5rem' }}>Você ainda está aí?</h3>
        <p style={{ color: '#4a5568', fontSize: '0.92rem', marginBottom: '1.1rem' }}>
          Por segurança, sua sessão será encerrada em <strong data-testid="idle-warning-seconds">{left}s</strong> por inatividade.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button variant="outline" onClick={() => logout('manual')} data-testid="idle-logout-btn">Sair</Button>
          <Button onClick={keepAlive} data-testid="idle-stay-btn">Continuar conectado</Button>
        </div>
      </div>
    </div>
  );
};
